"use client";

import * as React from "react";
import { ArrowRight, Building2, Container, Ship, Truck } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const galleryItems = [{
  id: 1,
  title: "STRUCTURAL FOUNDATIONS",
  category: "COMMERCIAL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568171/img/m2_logistics_warehouse_interior.png",
  icon: Building2,
  description: "Precision-engineered footings and reinforced walls designed for large-scale industrial developments."
}, {
  id: 2,
  title: "CUSTOM DRIVEWAYS",
  category: "RESIDENTIAL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568171/img/m2_logistics_fleet_trucks.png",
  icon: Truck,
  description: "Durable, Midwest-ready concrete solutions built to withstand Indiana's harshest seasonal cycles."
}, {
  id: 3,
  title: "DECORATIVE PATIOS",
  category: "STAMPED",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568171/img/m2_logistics_cargo_ship_deck.png",
  icon: Ship,
  description: "Artisan stamped concrete patterns that provide the look of natural stone with superior durability."
}, {
  id: 4,
  title: "VERTICAL CONCRETE",
  category: "SPECIALTY",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568171/img/m2_logistics_port_crane_loading.png",
  icon: Container,
  description: "Architectural vertical surfaces featuring unique textures for modern retaining walls and facades."
}, {
  id: 5,
  title: "INDUSTRIAL FLOORING",
  category: "COMMERCIAL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568171/img/m2_logistics_office_headquarters.png",
  icon: Building2,
  description: "High-performance slab work and broom finishes for warehouses and heavy-duty manufacturing facilities."
}];
export default function Gallery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return <section data-section-id="33650" id="carousel" ref={ref} className={cn("bg-background py-24 overflow-hidden transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-12 md:mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif uppercase tracking-tighter text-foreground mb-4">
            PROJECT PORTFOLIO
          </h2>
          <p className="text-muted-foreground text-lg font-sans">
            A showcase of nearly 20 years of craftsmanship in residential and commercial concrete across Central Indiana.
          </p>
        </div>

        <div className="relative">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {galleryItems.map((item, i) => <CarouselItem key={item.id} data-index={i} className="pl-4 md:pl-6 basis-[85%] md:basis-[60%] lg:basis-[45%]">
                  <div className="h-full py-2">
                    <Card className="h-full border-0 shadow-xl bg-card overflow-hidden group rounded-none transition-transform duration-500 hover:-translate-y-1">
                      <CardContent className="p-0 h-full flex flex-col">
                        <div className="relative w-full overflow-hidden">
                          <AspectRatio ratio={16 / 10}>
                            <img src={item.image} alt={item.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105 filter grayscale group-hover:grayscale-0" />
                          </AspectRatio>
                          <div className="absolute top-4 left-4 bg-background/95 backdrop-blur-sm px-4 py-1.5 rounded-none text-[10px] tracking-widest uppercase font-bold text-foreground shadow-md flex items-center gap-2 border-l-2 border-primary">
                            <item.icon className="w-3.5 h-3.5 text-primary" />
                            {item.category}
                          </div>
                        </div>
                        
                        <div className="p-8 flex flex-col grow justify-between bg-card">
                          <div>
                            <h3 className="text-2xl font-serif uppercase tracking-tight text-foreground mb-3 group-hover:text-primary transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                              {item.description}
                            </p>
                          </div>
                          <div className="mt-6 pt-6 border-t border-border flex items-center text-xs font-bold uppercase tracking-widest text-primary opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                            VIEW PROJECT <ArrowRight className="ml-2 w-4 h-4" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            <div className="hidden md:block">
              <CarouselPrevious className="left-4 lg:-left-12 bg-background/90 rounded-none h-12 w-12 hover:bg-primary hover:text-primary-foreground border-primary" />
              <CarouselNext className="right-4 lg:-right-12 bg-background/90 rounded-none h-12 w-12 hover:bg-primary hover:text-primary-foreground border-primary" />
            </div>
          </Carousel>

          {}
          <div className="flex justify-center gap-3 mt-8 md:mt-12">
            {Array.from({
            length: count
          }).map((_, index) => <button key={index} onClick={() => api?.scrollTo(index)} className={cn("h-1.5 transition-all duration-500 focus:outline-none", current === index + 1 ? "w-12 bg-primary" : "w-4 bg-muted-foreground/20 hover:bg-muted-foreground/40")} aria-label={`Go to slide ${index + 1}`} />)}
          </div>
        </div>
      </div>
    </section>;
}